import Instance from "../types/Instance";
import { gzip } from "fflate";

/**
 * Promisified wrapper around fflate's async `gzip`. Unlike `gzipSync`, this runs
 * the deflate on a worker thread, so a large multiframe instance (whose whole
 * pixel payload sits in one buffer) doesn't block the main thread / freeze the
 * UI while it compresses.
 *
 * `consume: true` is critical for memory: without it fflate structured-CLONES
 * the input buffer into the worker (postMessage with an empty transfer list), so
 * a large P10 is held twice — once on the main thread, once in the worker —
 * ~doubling heap during compression. `consume` instead TRANSFERS the buffer to
 * the worker (it's neutered on the main thread), so only one copy exists at a
 * time. The caller must not touch the input after this resolves — gzipInstance
 * nulls it immediately, so that holds.
 */
function gzipAsync(data: Uint8Array): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    gzip(data, { consume: true }, (err, out) => (err ? reject(err) : resolve(out)));
  });
}

/**
 * Replaces the part10ArrayBuffer with a Gzipped version and records the size before and after on
 * the Instance object.
 * @param instance
 */
export default async function gzipInstance(instance: Instance) {
  if (!instance.instanceData.dicomP10AnonymizedArrayBuffer) {
    throw new Error(
      `No anonymized Part 10 Array buffer to compress for instance with uid ${instance.uid}`
    );
  }
  instance.instanceData.p10Size = {
    decompressed:
      instance.instanceData.dicomP10AnonymizedArrayBuffer.byteLength,
  };

  const deflatedUint8Array = await gzipAsync(
    new Uint8Array(instance.instanceData.dicomP10AnonymizedArrayBuffer)
  );
  instance.instanceData.dicomP10Compressed = new Blob(
    [deflatedUint8Array as Uint8Array<ArrayBuffer>],
    { type: "application/dicom" }
  );
  instance.instanceData.p10Size.compressed =
    instance.instanceData.dicomP10Compressed.size;

  // Allow uncompressed array buffer to be garbage collected
  instance.instanceData.dicomP10AnonymizedArrayBuffer = null;

  return instance;
}
